import {produce} from 'immer';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {uniqueId} from 'lodash';
import {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import toast from '@/components/HotToast';
import {ArrowLeftIcon, ArrowRightIcon} from '@/components/Icons';
import {AnalysisItem} from '@/map/@types';
import {useFetchAiPrompt, useQueryBasicAnalysis} from '@/map/api';
import {
  activeAnalysisPanelAtom, analysisDrawItemAtom, anaysisListAtom, compareAreaItemsAtom
} from '@/map/atom';
import {AREA_OVERLAY_TYPE_TEXT, INVALID_POLYGON_ERROR} from '@/map/constants';
import {AreaOverlayType} from '@/map/enums';
import {ExtendedFloatPaneOptions, panToBoundCircle} from '@/map/modules';
import {FloatPane} from '@/map/modules/Overlay';
import {
  convertToPolygonPathsFromGeometry, distanceToString, getAreaAnalyticsErrorMessage, getKm2,
  getMaxPoygon, getPolygonPaths, getSidoShortName, getZoomLevel
} from '@/map/utils';
import {interceptProps, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {useAiPromptStack} from '../../AiPrompt/hook';
import {AiToggle} from './AiToggle';
import {Delivery} from './Delivery';
import {ErrorBox} from './ErrorBox';
import {FootTraffic} from './FootTraffic';
import {HallSales} from './HallSales';
import {Populations} from './Populations';
import {RadiusDropdown} from './RadiusDropdown';
import {Container, WindowBody} from './StyledComponents';
import {TitleBar} from './TitleBar';

interface IProps {
  map: naver.maps.Map;
  item: AnalysisItem;
  remove?(): void;
}

export const getPenalPosition = (item: AnalysisItem, isLeft?: boolean) => {
  const position: any = {
    transform: {y: '-50%', x: isLeft ? '-100%' : '0'},
  };

  if (item[AreaOverlayType.POLYGON]) {
    if (item.polygon.paths) {
      const paths = getPolygonPaths(item.polygon.paths);
      const point = paths.sort(
        (a: naver.maps.Point, b: naver.maps.Point) => b.x - a.x
      )[isLeft ? paths.length - 1 : 0];

      position.position = point;
    }
  } else if (item[AreaOverlayType.CIRCLE]) {
    const {radius, center} = item.circle;

    if (!radius) {
      return center;
    }

    const point = (center as naver.maps.LatLng).destinationPoint(
      isLeft ? 270 : 90,
      radius
    );
    position.position = point;
  } else if (
    item[AreaOverlayType.EMD] &&
    item[AreaOverlayType.EMD].geometry?.coordinates
  ) {
    const polygons = convertToPolygonPathsFromGeometry(
      item[AreaOverlayType.EMD].geometry.coordinates
    );
    const {polygon} = getMaxPoygon(polygons);

    const paths = getPolygonPaths(polygon as any);
    const point = paths.sort(
      (a: naver.maps.Point, b: naver.maps.Point) => b.x - a.x
    )[isLeft ? paths.length - 1 : 0];

    position.position = point;
  }

  return position;
};

const getTitle = (item: AnalysisItem) => {
  if (item[AreaOverlayType.POLYGON]?.m2) {
    return `면적 ${getKm2(item[AreaOverlayType.POLYGON].m2)}k㎡`;
  } else if (item[AreaOverlayType.CIRCLE]) {
    return `${distanceToString(item.circle.radius ?? 0, {
      roundingMode: 'ceil',
    })}`;
  } else if (item[AreaOverlayType.EMD]?.name) {
    return getSidoShortName(item[AreaOverlayType.EMD].name);
  } else {
    return '선택지역';
  }
};

const getTypeText = (item: AnalysisItem) => {
  return AREA_OVERLAY_TYPE_TEXT[item.type];
};

export const AnalysisPanel = ({map, item, remove}: IProps) => {
  const setAnalysisList = useSetAtom(anaysisListAtom);
  const setDrawItem = useSetAtom(analysisDrawItemAtom);
  const [isLeft, setPanelLeft] = useState<boolean>(false);
  const [positionInfo, setPosition] = useState<
    ExtendedFloatPaneOptions['positionInfo']
  >(getPenalPosition(item, isLeft));
  const [activePanelId, setActivePenalId] = useAtom(activeAnalysisPanelAtom);
  const {activeAiPropmptItem, createAiPrompt, setActiveAiPromptItem} =
    useAiPromptStack();

  const {data, isPending, error} = useQueryBasicAnalysis(item);
  const errorMessage = !!error
    ? getAreaAnalyticsErrorMessage(error)
    : undefined;

  useFetchAiPrompt(item.id, data);

  const selectedCompareIds = useAtomValue(compareAreaItemsAtom);

  const handleClick = useCallback(() => {
    if (!item?.id) {
      return;
    }

    setActivePenalId(item.id);

    if (map && map.getZoom() <= getZoomLevel(10_000)) {
      map.morph(positionInfo.position, getZoomLevel(3_000));
    }
  }, [item?.id, map, positionInfo?.position, setActivePenalId]);

  const onChangeDirection = useCallback(() => {
    setPanelLeft((prev) => !prev);
  }, []);

  const onClosePanel = () => {
    if (!item?.id) {
      return;
    }

    if (selectedCompareIds.includes(item.id)) {
      toast.error('비교 중인 상권은 변경하거나 닫을 수 없습니다.');
    } else {
      remove?.();
    }
  };

  const changeRadius = useCallback(
    (radius: number | 'reset') => {
      if (!item?.id) {
        return;
      }

      const circle = item[AreaOverlayType.CIRCLE];

      if (item.type !== AreaOverlayType.CIRCLE || !circle) {
        return;
      }

      if (selectedCompareIds.includes(item.id)) {
        toast.error('비교 중인 상권은 변경하거나 닫을 수 없습니다.');
        return;
      }

      if (radius === 'reset') {
        remove?.();
        setDrawItem({
          type: item.type,
        });
      } else if (radius !== circle.radius) {
        setAnalysisList(
          produce((draft) => {
            const index = draft.findIndex((d) => d.id === item.id);
            if (index !== -1) draft.splice(index, 1);
            draft.push({
              ...item,
              id: uniqueId(`${item.type}-`),
              [AreaOverlayType.CIRCLE]: {
                ...circle,
                radius,
              },
            });
          })
        );

        if (map && circle.center && radius) {
          panToBoundCircle({center: circle.center, radius}, map, {
            left: 500,
            right: 350,
          });
        }
      }
    },
    [item, map, selectedCompareIds, remove, setAnalysisList, setDrawItem]
  );

  const onToggleAiPrompt = (open: boolean) => {
    setActiveAiPromptItem({open, id: item.id});
  };

  useEffect(() => {
    setPosition(getPenalPosition(item, isLeft));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeft]);

  useEffect(() => {
    if (data && item?.id) {
      createAiPrompt(item.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!map || !positionInfo?.position || !item?.id) {
    return null;
  } else if (errorMessage?.includes(INVALID_POLYGON_ERROR)) {
    return (
      <FloatPane
        map={map}
        positionInfo={positionInfo}
        zIndex={activePanelId === item.id ? 1 : 0}
      >
        <Container
          marginDirection={isLeft ? 'right' : 'left'}
          onClick={handleClick}
        >
          <TitleBar onClose={onClosePanel} />
          <ErrorBox />
        </Container>
      </FloatPane>
    );
  }

  const isCircle = item.type === AreaOverlayType.CIRCLE;

  return (
    <FloatPane
      map={map}
      positionInfo={positionInfo}
      zIndex={activePanelId === item.id ? 1 : 0}
    >
      <Container
        marginDirection={isLeft ? 'right' : 'left'}
        onClick={handleClick}
      >
        <DirectionSwitchButton
          isRight={isLeft}
          aria-label="창 방향 이동"
          onClick={onChangeDirection}
        />
        <TitleBar
          prefix={getTypeText(item)}
          title={isCircle ? undefined : getTitle(item)}
          onClose={onClosePanel}
        >
          {isCircle && (
            <>
              <RadiusDropdown
                radius={item[AreaOverlayType.CIRCLE]?.radius}
                onChange={changeRadius}
              />
            </>
          )}
          <PaddingBox left={isCircle ? 12 : 8}>
            <AiToggle
              active={
                activeAiPropmptItem.id === item.id && activeAiPropmptItem.open
              }
              analysisType={getTypeText(item)}
              onToggle={onToggleAiPrompt}
            />
          </PaddingBox>
        </TitleBar>
        <WindowBody>
          <Populations data={data} isPending={isPending} error={errorMessage} />
          <FootTraffic item={item} />
          <Delivery item={item} />
          <HallSales
            item={item}
            data={data?.hallSales}
            isPending={isPending}
            error={errorMessage}
          />
        </WindowBody>
      </Container>
    </FloatPane>
  );
};

const DirectionSwitchButton = styled('button').withConfig({
  shouldForwardProp: interceptProps(['isRight']),
})<{
  isRight?: boolean;
}>`
  width: 28px;
  height: 28px;
  top: 12px;
  position: absolute;
  cursor: pointer;
  background-position: center;
  background-repeat: no-repeat;
  border: solid 1px ${palette.gray300};
  background-color: ${palette.white};
  ${({isRight}) =>
    isRight
      ? `
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
      right: -11px;
      background-image: url(${ArrowRightIcon});
      `
      : `
      border-top-left-radius: 5px;
      border-bottom-left-radius: 5px;
      left: -11px;
      background-image: url(${ArrowLeftIcon});
  `}
`;
